import { useCallback, useEffect, useState } from 'react';
import {
  loadSubtitles, loadTimedAnnotations, loadTitleAnnotations, loadTitleSubtitle,
} from './subtitleStore';
import { byTitleOrder } from '../../utils/timeline';

const byStart = (a, b) => a.startTime - b.startTime;

/**
 * The teacher's view of a video's content: its subtitles and its
 * subtitle-free annotations, shared by the "Now" panel and the lists, so an
 * edit in one shows in the others.
 */
export default function useVideoContent(videoId) {
  const [subtitles, setSubtitles] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [titleAnnotations, setTitleAnnotations] = useState([]);
  const [titleSubtitle, setTitleSubtitle] = useState(null);
  const [loading, setLoading] = useState(false);

  const reloadSubtitles = useCallback(() => {
    if (!videoId) return Promise.resolve();
    return loadSubtitles(videoId).then(setSubtitles, (e) => console.error('Error loading subtitles:', e));
  }, [videoId]);

  const reloadAnnotations = useCallback(() => {
    if (!videoId) return Promise.resolve();
    return loadTimedAnnotations(videoId).then(setAnnotations, (e) => console.error('Error loading annotations:', e));
  }, [videoId]);

  const reloadTitleAnnotations = useCallback(() => {
    if (!videoId) return Promise.resolve();
    return Promise.all([
      loadTitleAnnotations(videoId).then(setTitleAnnotations),
      loadTitleSubtitle(videoId).then(setTitleSubtitle),
    ]).catch((e) => console.error('Error loading the title\'s subtitle and annotations:', e));
  }, [videoId]);

  useEffect(() => {
    setSubtitles([]);
    setAnnotations([]);
    setTitleAnnotations([]);
    setTitleSubtitle(null);
    if (!videoId) return;
    setLoading(true);
    Promise.all([reloadSubtitles(), reloadAnnotations(), reloadTitleAnnotations()]).then(() => setLoading(false));
  }, [videoId, reloadSubtitles, reloadAnnotations, reloadTitleAnnotations]);

  const upsertAnnotation = useCallback((a) => setAnnotations((prev) => [
    ...prev.filter((x) => x.id !== a.id), a,
  ].sort(byStart)), []);

  const removeAnnotation = useCallback((id) => setAnnotations((prev) => prev.filter((x) => x.id !== id)), []);

  const upsertSubtitle = useCallback((s) => setSubtitles((prev) => [
    ...prev.filter((x) => x.id !== s.id), s,
  ].sort(byStart)), []);

  // Title annotations (no times) are kept apart from the timed ones.
  const upsertTitleAnnotation = useCallback((a) => setTitleAnnotations((prev) => [
    ...prev.filter((x) => x.id !== a.id), a,
  ].sort(byTitleOrder)), []);
  const removeTitleAnnotation = useCallback((id) => setTitleAnnotations((prev) => prev.filter((x) => x.id !== id)), []);

  return {
    subtitles, annotations, titleAnnotations, titleSubtitle, setTitleSubtitle, loading,
    reloadSubtitles, reloadAnnotations, upsertAnnotation, removeAnnotation, upsertSubtitle,
    upsertTitleAnnotation, removeTitleAnnotation,
  };
}
